import { useEffect, useState } from 'react';
import type { BossDifficulty } from '@shared/boss';
import { BackButton } from '@/components/BackButton';
import { audio } from '@/audio/AudioService';
import { useBossStore } from '@/stores/bossStore';
import { useUIStore } from '@/stores/uiStore';
import type { BossListItem } from '@/boss/bossApi';
import type { BossCompetitionOverview } from '@/boss/bossApi';
import { useT } from '@/i18n/useI18n';
import { resolveAssetUrl } from '@/shop/assetUrl';
import { STONE_KING_ARENA_IMAGE } from '@/boss/bossAssets';
import {
  localizedBossDescription,
  localizedBossName,
} from '@/boss/bossLocalization';

export function BossSelectScreen() {
  const t = useT();
  const go = useUIStore((state) => state.go);
  const enabled = useBossStore((state) => state.enabled);
  const bosses = useBossStore((state) => state.bosses);
  const rotation = useBossStore((state) => state.rotation);
  const competition = useBossStore((state) => state.competition);
  const loading = useBossStore((state) => state.loading);
  const error = useBossStore((state) => state.error);
  const load = useBossStore((state) => state.load);
  const start = useBossStore((state) => state.start);
  const [starting, setStarting] = useState<string | null>(null);
  const [ladderScope, setLadderScope] = useState<'daily' | 'weekly'>('daily');
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const urls = [
      STONE_KING_ARENA_IMAGE,
      ...bosses.flatMap((boss) => (boss.imageUrl ? [boss.imageUrl] : [])),
    ];
    urls.forEach((src) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
    });
  }, [bosses]);

  const begin = async (
    boss: BossListItem,
    difficulty: BossDifficulty,
  ): Promise<void> => {
    const key = `${boss.id}:${difficulty}`;
    setStarting(key);
    audio.play('ui.tap_primary');
    try {
      await start(boss.id, difficulty);
      go('bossBattle');
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="screen boss-select-screen">
      <div className="hex-bg" />
      <header className="boss-select-header">
        <BackButton onClick={() => go('home')} />
        <div>
          <div className="tag" style={{ color: 'var(--c-red)' }}>
            {t('boss.mode_label')}
          </div>
          <h1 className="display shadow-text">{t('boss.title')}</h1>
          <p>{t('boss.subtitle')}</p>
        </div>
      </header>

      {competition?.enabled && (
        <BossCompetitionPanel
          competition={competition}
          scope={ladderScope}
          onScope={setLadderScope}
          onOpen={() => setLeaderboardOpen(true)}
        />
      )}

      {rotation && (
        <section className="boss-weekly">
          <div className="boss-weekly-head">
            <span className="tag">{t('boss.weekly_hunt')}</span>
            <b>{rotation.clearedCount}/7</b>
          </div>
          <div className="boss-weekly-days">
            {rotation.schedule.map((entry) => (
              <div
                key={entry.day}
                className={`boss-day ${
                  entry.isToday ? 'is-today' : ''
                } ${entry.cleared ? 'is-cleared' : ''}`}
                style={
                  { '--day-accent': entry.accentColor } as React.CSSProperties
                }
                title={localizedBossName(t, entry)}
              >
                <small>D{entry.day}</small>
                {entry.imageUrl ? <img src={entry.imageUrl} alt="" /> : <b>?</b>}
                <i>{entry.cleared ? '✓' : entry.isToday ? '●' : ''}</i>
              </div>
            ))}
          </div>
          <div className="boss-weekly-milestones">
            {rotation.milestones.map((milestone) => (
              <span
                key={milestone.required}
                className={milestone.claimed ? 'is-claimed' : ''}
              >
                {milestone.required}/7 ·
                {milestone.coins > 0 ? ` ${milestone.coins}🪙` : ''}
                {milestone.gems > 0 ? ` ${milestone.gems}💎` : ''}
                {milestone.frame ? ` ${t('boss.legendary_frame')}` : ''}
                {milestone.claimed ? ' ✓' : ''}
              </span>
            ))}
          </div>
        </section>
      )}

      <main className="boss-list">
        {loading && bosses.length === 0 && (
          <div className="boss-empty">{t('boss.loading')}</div>
        )}
        {!loading && !enabled && (
          <div className="boss-empty">
            <strong>{t('boss.disabled_title')}</strong>
            <span>{t('boss.disabled_body')}</span>
          </div>
        )}
        {error && <div className="boss-error">{t('boss.load_error')}</div>}
        {bosses.map((boss) => (
          <BossCard
            key={boss.id}
            boss={boss}
            starting={starting}
            rankedAvailable={
              !competition?.enabled
              || (competition.rankingOpen
                && competition.attemptsRemaining > 0)
            }
            unavailableLabel={
              competition?.rankingOpen
                ? t('boss.competition.attempts', {
                    remaining: competition.attemptsRemaining,
                    total: competition.dailyRunLimit,
                  })
                : t('boss.competition.closed')
            }
            onBegin={begin}
          />
        ))}
      </main>

      {competition?.enabled && leaderboardOpen && (
        <BossLeaderboardModal
          competition={competition}
          scope={ladderScope}
          onScope={setLadderScope}
          onClose={() => setLeaderboardOpen(false)}
        />
      )}
    </div>
  );
}

function BossCompetitionPanel({
  competition,
  scope,
  onScope,
  onOpen,
}: {
  competition: BossCompetitionOverview;
  scope: 'daily' | 'weekly';
  onScope: (scope: 'daily' | 'weekly') => void;
  onOpen: () => void;
}) {
  const t = useT();
  return (
    <section className="boss-competition">
      <div className="boss-competition__head">
        <div>
          <span className="tag">{t('boss.competition.title')}</span>
          <b>
            {t('boss.competition.attempts', {
              remaining: competition.attemptsRemaining,
              total: competition.dailyRunLimit,
            })}
          </b>
        </div>
        <small>
          {competition.rankingOpen
            ? t('boss.competition.closes', {
                time: new Date(competition.weekEndsAt).toLocaleString(),
              })
            : t('boss.competition.closed')}
        </small>
      </div>
      <div className="boss-competition__summary">
        <button
          className={scope === 'daily' ? 'is-active' : ''}
          onClick={() => {
            onScope('daily');
            onOpen();
          }}
        >
          <span>{t('boss.competition.daily')}</span>
          <b>
            {(
              competition.myDailyScore ?? competition.myDailyDamage
            ).toLocaleString()}
          </b>
          <small>
            {t('boss.competition.my_rank', {
              rank: competition.myDailyRank ?? '—',
            })}
          </small>
        </button>
        <button
          className={scope === 'weekly' ? 'is-active' : ''}
          onClick={() => {
            onScope('weekly');
            onOpen();
          }}
        >
          <span>{t('boss.competition.weekly')}</span>
          <b>{competition.myWeeklyPoints.toLocaleString()}</b>
          <small>
            {t('boss.competition.my_rank', {
              rank: competition.myWeeklyRank ?? '—',
            })}
          </small>
        </button>
      </div>
      <button className="boss-competition__open" onClick={onOpen}>
        <span>🏆</span>
        {t('boss.competition.open')}
        <i>›</i>
      </button>
    </section>
  );
}

function BossLeaderboardModal({
  competition,
  scope,
  onScope,
  onClose,
}: {
  competition: BossCompetitionOverview;
  scope: 'daily' | 'weekly';
  onScope: (scope: 'daily' | 'weekly') => void;
  onClose: () => void;
}) {
  const t = useT();
  const entries =
    scope === 'daily' ? competition.dailyTop : competition.weeklyTop;
  const scoreLabel =
    scope === 'daily'
      ? t('boss.competition.damage')
      : t('boss.competition.points');
  const myScore =
    scope === 'daily'
      ? competition.myDailyScore ?? competition.myDailyDamage
      : competition.myWeeklyPoints;
  const myRank =
    scope === 'daily'
      ? competition.myDailyRank
      : competition.myWeeklyRank;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="boss-leaderboard-backdrop" onClick={onClose}>
      <section
        className="boss-leaderboard-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="boss-leaderboard-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="boss-leaderboard-modal__head">
          <div>
            <span className="tag">{t('boss.mode_label')}</span>
            <h2 id="boss-leaderboard-title" className="display">
              {t('boss.competition.title')}
            </h2>
          </div>
          <button
            className="boss-leaderboard-modal__close"
            onClick={onClose}
            aria-label={t('boss.competition.close')}
          >
            ×
          </button>
        </header>

        <div className="boss-leaderboard-modal__tabs">
          <button
            className={scope === 'daily' ? 'is-active' : ''}
            onClick={() => onScope('daily')}
          >
            {t('boss.competition.daily')}
          </button>
          <button
            className={scope === 'weekly' ? 'is-active' : ''}
            onClick={() => onScope('weekly')}
          >
            {t('boss.competition.weekly')}
          </button>
        </div>

        <div className="boss-leaderboard-modal__personal">
          <div>
            <small>{scoreLabel}</small>
            <strong>{myScore.toLocaleString()}</strong>
          </div>
          <div>
            <small>{t('boss.competition.rank')}</small>
            <strong>#{myRank ?? '—'}</strong>
          </div>
          <div>
            <small>{t('boss.competition.attempts_label')}</small>
            <strong>
              {competition.attemptsRemaining}/{competition.dailyRunLimit}
            </strong>
          </div>
        </div>

        <div className="boss-leaderboard-modal__list">
          {entries.length === 0 ? (
            <div className="boss-leaderboard-modal__empty">
              {t('boss.competition.empty')}
            </div>
          ) : (
            entries.slice(0, 20).map((entry) => (
              <div
                key={`${scope}-${entry.userId}`}
                className={
                  entry.rank <= 3 ? `is-top-${entry.rank}` : undefined
                }
              >
                <b>#{entry.rank}</b>
                <span>
                  <strong>{entry.displayName}</strong>
                  <small>{entry.rankTier}</small>
                </span>
                <em>
                  {entry.score.toLocaleString()}
                  <small>{scoreLabel}</small>
                </em>
              </div>
            ))
          )}
        </div>

        <footer className="boss-leaderboard-modal__foot">
          <span>
            {t('boss.competition.attempts', {
              remaining: competition.attemptsRemaining,
              total: competition.dailyRunLimit,
            })}
          </span>
          <button onClick={onClose}>{t('boss.competition.close')}</button>
        </footer>
      </section>
    </div>
  );
}

function BossCard({
  boss,
  starting,
  rankedAvailable,
  unavailableLabel,
  onBegin,
}: {
  boss: BossListItem;
  starting: string | null;
  rankedAvailable: boolean;
  unavailableLabel: string;
  onBegin: (boss: BossListItem, difficulty: BossDifficulty) => Promise<void>;
}) {
  const t = useT();
  const bossName = localizedBossName(t, boss);
  const bossDescription = localizedBossDescription(t, boss);
  const normalLabel = t('boss.difficulty.normal');
  const hardLabel = t('boss.difficulty.hard');

  return (
    <article
      className="boss-card"
      style={{ '--boss-accent': boss.accentColor } as React.CSSProperties}
    >
      <div className="boss-card-art">
        {boss.imageUrl ? (
          <img src={boss.imageUrl} alt="" />
        ) : (
          <StoneBossMini />
        )}
        <div className="boss-card-level">{t('boss.world_boss')}</div>
        {boss.rewards.frame && (
          <div
            className={`boss-frame-reward ${
              boss.rewards.frame.owned ? 'is-owned' : ''
            }`}
          >
            {boss.rewards.frame.iconUrl && (
              <img
                src={resolveAssetUrl(boss.rewards.frame.iconUrl) ?? ''}
                alt=""
              />
            )}
            <span>
              <b>
                {boss.rewards.frame.owned
                  ? t('boss.frame_owned')
                  : t('boss.frame_reward')}
              </b>
              <small>
                {boss.rewards.frame.owned
                  ? boss.rewards.frame.name
                  : t('boss.frame_requirement', {
                      rate: boss.rewards.frame.requiredPerfectRate,
                    })}
              </small>
            </span>
          </div>
        )}
      </div>
      <div className="boss-card-body">
        <div className="boss-card-title">
          <div>
            <div className="tag">{t('boss.target')}</div>
            <h2 className="display">{bossName}</h2>
          </div>
          <span className="mono">{Math.round(boss.durationMs / 1000)}s</span>
        </div>
        <p>{bossDescription}</p>
        <div className="boss-records">
          <span>
            {normalLabel} · {t('boss.wins', { n: boss.progress.normalVictories })}
            {boss.progress.bestNormalTimeMs != null
              ? ` · ${(boss.progress.bestNormalTimeMs / 1000).toFixed(1)}s`
              : ''}
          </span>
          <span>
            {hardLabel} · {t('boss.wins', { n: boss.progress.hardVictories })}
            {boss.progress.bestHardTimeMs != null
              ? ` · ${(boss.progress.bestHardTimeMs / 1000).toFixed(1)}s`
              : ''}
          </span>
        </div>
        <div className="boss-difficulty-actions">
          <button
            className="btn btn-primary"
            disabled={starting != null || !rankedAvailable}
            onClick={() => void onBegin(boss, 'normal')}
          >
            {!rankedAvailable
              ? unavailableLabel
              : starting === `${boss.id}:normal`
              ? t('boss.starting')
              : `${normalLabel} · +${boss.rewards.normalCoins} 🪙`}
          </button>
          <button
            className="btn boss-hard-btn"
            disabled={
              !boss.hardUnlocked || starting != null || !rankedAvailable
            }
            onClick={() => void onBegin(boss, 'hard')}
          >
            {!rankedAvailable
              ? unavailableLabel
              : boss.hardUnlocked
              ? starting === `${boss.id}:hard`
                ? t('boss.starting')
                : `${hardLabel} · +${boss.rewards.hardCoins} 🪙`
              : t('boss.unlock_hard')}
          </button>
        </div>
        <div className="boss-daily-note">
          {t('boss.daily_note', {
            gems: boss.rewards.firstClearGems,
            limit: boss.rewards.dailyLimit,
          })}
        </div>
      </div>
    </article>
  );
}

function StoneBossMini() {
  return (
    <div className="stone-boss stone-boss--mini" aria-hidden="true">
      <div className="stone-boss-horn stone-boss-horn--left" />
      <div className="stone-boss-horn stone-boss-horn--right" />
      <div className="stone-boss-head">
        <i />
        <i />
      </div>
      <div className="stone-boss-body">
        <b />
        <b />
        <b />
        <b />
      </div>
    </div>
  );
}
